import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getJsonHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
  Lang: i18n.language,
});

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  Lang: i18n.language,
});

const appendParams = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === "" || value === null || value === undefined) return;
    query.append(key, String(value));
  });
  return query.toString();
};

const extractListPayload = (responseData) => {
  const root = responseData?.data ?? responseData;
  const rows = Array.isArray(root?.data)
    ? root.data
    : Array.isArray(root)
      ? root
      : [];
  const pagination = root?.pagination || responseData?.pagination || null;
  const summary = root?.summary || null;
  return { rows, pagination, summary };
};

const extractError = (err, fallback) =>
  err.response?.data?.message || fallback;

export const fetchLeads = createAsyncThunk(
  "leads/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const qs = appendParams({ per_page: 20, page: 1, ...params });
      const res = await axios.get(`${BASE_URL}/leads${qs ? `?${qs}` : ""}`, {
        headers: getJsonHeaders(),
      });
      const { rows, pagination, summary } = extractListPayload(res.data);
      return { data: rows, pagination, summary };
    } catch (err) {
      return rejectWithValue(extractError(err, "Failed to fetch leads"));
    }
  }
);

export const fetchLeadsFollowUps = createAsyncThunk(
  "leads/fetchFollowUps",
  async (params = {}, { rejectWithValue }) => {
    try {
      const qs = appendParams({ per_page: 20, page: 1, overdue: 1, ...params });
      const res = await axios.get(
        `${BASE_URL}/leads/follow-ups${qs ? `?${qs}` : ""}`,
        { headers: getJsonHeaders() }
      );
      const { rows, pagination, summary } = extractListPayload(res.data);
      return { data: rows, pagination, summary };
    } catch (err) {
      return rejectWithValue(extractError(err, "Failed to fetch follow-ups"));
    }
  }
);

export const fetchLeadById = createAsyncThunk(
  "leads/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/leads/${id}`, {
        headers: getJsonHeaders(),
      });
      return res.data?.data ?? res.data;
    } catch (err) {
      return rejectWithValue(extractError(err, "Failed to fetch lead"));
    }
  }
);

export const createLead = createAsyncThunk(
  "leads/create",
  async (formData, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${BASE_URL}/leads`, formData, {
        headers: getAuthHeaders(),
      });
      if (res.data?.status === false) {
        return rejectWithValue(res.data?.message || "Failed to create lead");
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(extractError(err, "Failed to create lead"));
    }
  }
);

export const updateLead = createAsyncThunk(
  "leads/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${BASE_URL}/leads/update/${id}`,
        payload,
        { headers: getJsonHeaders() }
      );
      if (res.data?.status === false) {
        return rejectWithValue(res.data?.message || "Failed to update lead");
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(extractError(err, "Failed to update lead"));
    }
  }
);

export const updateLeadStatus = createAsyncThunk(
  "leads/updateStatus",
  async ({ id, status, notes = "" }, { rejectWithValue }) => {
    try {
      const body = new FormData();
      body.append("status", status);
      if (notes != null && notes !== "") body.append("notes", notes);
      const res = await axios.post(`${BASE_URL}/leads/${id}/status`, body, {
        headers: getAuthHeaders(),
      });
      if (res.data?.status === false) {
        return rejectWithValue(
          res.data?.message || "Failed to update lead status"
        );
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(extractError(err, "Failed to update lead status"));
    }
  }
);

export const deleteLead = createAsyncThunk(
  "leads/delete",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${BASE_URL}/leads/delete/${id}`,
        {},
        { headers: getJsonHeaders() }
      );
      if (res.data?.status === false) {
        return rejectWithValue(res.data?.message || "Failed to delete lead");
      }
      return { id, ...(res.data || {}) };
    } catch (err) {
      return rejectWithValue(extractError(err, "Failed to delete lead"));
    }
  }
);

export const fetchLeadActivities = createAsyncThunk(
  "leads/fetchActivities",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/leads/${id}/activities`, {
        headers: getJsonHeaders(),
      });
      const root = res.data?.data ?? res.data;
      const rows = Array.isArray(root)
        ? root
        : Array.isArray(root?.data)
          ? root.data
          : [];
      return { leadId: id, data: rows };
    } catch (err) {
      return rejectWithValue(
        extractError(err, "Failed to fetch lead activities")
      );
    }
  }
);

export const createLeadActivity = createAsyncThunk(
  "leads/createActivity",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${BASE_URL}/leads/${id}/activities`,
        formData,
        { headers: getAuthHeaders() }
      );
      if (res.data?.status === false) {
        return rejectWithValue(
          res.data?.message || "Failed to create lead activity"
        );
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(
        extractError(err, "Failed to create lead activity")
      );
    }
  }
);

const initialState = {
  list: { data: [], pagination: null, summary: null },
  record: null,
  activities: [],
  activitiesLoading: false,
  isLoading: false,
  detailLoading: false,
  isSaving: false,
  error: null,
  detailError: null,
  success: null,
};

const leadsSlice = createSlice({
  name: "leads",
  initialState,
  reducers: {
    clearLeadsState: (state) => {
      state.error = null;
      state.detailError = null;
      state.success = null;
    },
    clearLeadDetail: (state) => {
      state.record = null;
      state.activities = [];
      state.activitiesLoading = false;
      state.detailError = null;
      state.detailLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeads.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list = {
          data: action.payload.data || [],
          pagination: action.payload.pagination || null,
          summary: action.payload.summary || null,
        };
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.list = { data: [], pagination: null, summary: null };
      })
      .addCase(fetchLeadsFollowUps.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLeadsFollowUps.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list = {
          data: action.payload.data || [],
          pagination: action.payload.pagination || null,
          summary: action.payload.summary || null,
        };
      })
      .addCase(fetchLeadsFollowUps.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchLeadById.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchLeadById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.record = action.payload;
      })
      .addCase(fetchLeadById.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload;
        state.record = null;
      })
      .addCase(createLead.pending, (state) => {
        state.isSaving = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createLead.fulfilled, (state, action) => {
        state.isSaving = false;
        state.success = action.payload?.message || "created";
      })
      .addCase(createLead.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })
      .addCase(updateLead.pending, (state) => {
        state.isSaving = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateLead.fulfilled, (state, action) => {
        state.isSaving = false;
        state.success = action.payload?.message || "updated";
      })
      .addCase(updateLead.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })
      .addCase(updateLeadStatus.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateLeadStatus.fulfilled, (state, action) => {
        state.isSaving = false;
        state.success = action.payload?.message || "status updated";
      })
      .addCase(updateLeadStatus.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })
      .addCase(deleteLead.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(deleteLead.fulfilled, (state, action) => {
        state.isSaving = false;
        state.success = action.payload?.message || "deleted";
        const id = action.payload?.id;
        if (id != null && Array.isArray(state.list?.data)) {
          state.list.data = state.list.data.filter(
            (row) => String(row.id) !== String(id)
          );
        }
      })
      .addCase(deleteLead.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })
      .addCase(fetchLeadActivities.pending, (state) => {
        state.activitiesLoading = true;
      })
      .addCase(fetchLeadActivities.fulfilled, (state, action) => {
        state.activitiesLoading = false;
        state.activities = action.payload?.data || [];
      })
      .addCase(fetchLeadActivities.rejected, (state, action) => {
        state.activitiesLoading = false;
        state.activities = [];
        state.error = action.payload;
      })
      .addCase(createLeadActivity.pending, (state) => {
        state.isSaving = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createLeadActivity.fulfilled, (state, action) => {
        state.isSaving = false;
        state.success = action.payload?.message || "activity created";
      })
      .addCase(createLeadActivity.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      });
  },
});

export const { clearLeadsState, clearLeadDetail } = leadsSlice.actions;
export default leadsSlice.reducer;
